'use client'

import type { Entry, TopicRef } from '@/document'
import { cn } from '@/lib/tailwind'
import { MusicNoteIcon } from '@phosphor-icons/react'
import Image from 'next/image'

interface ArtistCardProps {
  artist: Entry & { topics?: TopicRef[] }
  className?: string
}

export function ArtistCard({ artist, className }: ArtistCardProps) {
  const metadata = artist.metadata as {
    imageUrl?: string
    bio?: string
  }
  const topics = artist.topics || []
  const hasImage = !!artist.imageUrl

  return (
    <div
      className={cn(
        'group hover:border-foreground/20 relative overflow-hidden rounded-lg border transition-colors',
        className,
      )}
    >
      {/* Image / Placeholder */}
      <div className="bg-muted relative aspect-square overflow-hidden">
        {hasImage ? (
          <Image
            src={artist.imageUrl!}
            alt={artist.title}
            fill
            className="object-cover transition-transform group-hover:scale-105"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <MusicNoteIcon className="text-muted-foreground/30 h-12 w-12" />
          </div>
        )}

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
      </div>

      {/* Info */}
      <div className="space-y-0.5 p-2.5">
        <h3 className="line-clamp-2 text-sm leading-tight font-medium">
          {artist.title}
        </h3>
        {/* Topics instead of genres */}
        {topics.length > 0 && (
          <p className="text-muted-foreground line-clamp-1 text-[11px]">
            {topics
              .slice(0, 3)
              .map((t) => t.name)
              .join(' · ')}
          </p>
        )}
        {metadata.bio && (
          <p className="text-muted-foreground/60 line-clamp-2 text-[11px] leading-snug">
            {metadata.bio}
          </p>
        )}
      </div>
    </div>
  )
}
